// Which company this tab is working in, for a person who belongs to several
// (multi-company login, backend `_specs/multi-company-login-spec.md` §5.1, §9.1).
//
// The choice is per TAB (sessionStorage), so two tabs can work in two companies
// at once. localStorage only remembers the last one per person, to highlight it
// on the picker; it never decides which company a request is for.
//
// A person with one company never has a stored company and never sends the
// header — their requests are exactly what they were before this existed.

import { TAB_ID_KEY } from '../utils/tabSession';

export const COMPANY_KEY = 'companyId';
export const COMPANY_HEADER = 'X-Company-Id';
// Set at sign-in when the person has to choose before anything else loads.
export const COMPANY_CHOICE_PENDING_KEY = 'companyChoicePending';
// Under the `bh.` prefix so the logout wipe spares it (utils/tabSession).
const LAST_COMPANY_PREFIX = 'bh.lastCompany.';

// The keys a company switch keeps: the login itself, and the tab's identity
// (clearing that silently empties the store on the next load — see tabSession).
export const SWITCH_KEPT_SESSION_KEYS = ['token', 'accessToken', 'cognitoUsername', TAB_ID_KEY];

/** A company id is a Mongo ObjectId. Anything else counts as no company. */
export const isValidCompanyId = (value) =>
  typeof value === 'string' && /^[0-9a-fA-F]{24}$/.test(value);

const read = (storage, key) => {
  try {
    return storage.getItem(key);
  } catch (e) {
    return null;
  }
};

export function getCompanyId() {
  const value = read(sessionStorage, COMPANY_KEY);
  return isValidCompanyId(value) ? value : null;
}

export function setCompanyId(companyId) {
  try {
    if (isValidCompanyId(companyId)) sessionStorage.setItem(COMPANY_KEY, companyId);
    else sessionStorage.removeItem(COMPANY_KEY);
  } catch (e) {
    // Storage throws in some private-browsing modes; no company is the safe answer.
  }
}

export function clearCompanyId() {
  setCompanyId(null);
}

/**
 * The header to merge into a request's headers. Empty when there is no valid
 * company — never `'null'`, `'undefined'` or `''`, which the server would have
 * to treat as absent anyway.
 */
export function companyHeader() {
  const companyId = getCompanyId();
  return companyId ? { [COMPANY_HEADER]: companyId } : {};
}

export function requireCompanyChoice() {
  try {
    sessionStorage.setItem(COMPANY_CHOICE_PENDING_KEY, '1');
  } catch (e) {
    // see setCompanyId
  }
}

export function clearCompanyChoice() {
  try {
    sessionStorage.removeItem(COMPANY_CHOICE_PENDING_KEY);
  } catch (e) {
    // see setCompanyId
  }
}

export const isCompanyChoicePending = () => read(sessionStorage, COMPANY_CHOICE_PENDING_KEY) === '1';

const lastCompanyKey = (cognitoUsername) =>
  cognitoUsername ? LAST_COMPANY_PREFIX + cognitoUsername : null;

/** For highlighting on the picker only. */
export function rememberLastCompany(cognitoUsername, companyId) {
  const key = lastCompanyKey(cognitoUsername);
  if (!key || !isValidCompanyId(companyId)) return;
  try {
    localStorage.setItem(key, companyId);
  } catch (e) {
    // see setCompanyId
  }
}

export function getLastCompany(cognitoUsername) {
  const key = lastCompanyKey(cognitoUsername);
  const value = key ? read(localStorage, key) : null;
  return isValidCompanyId(value) ? value : null;
}

/** Wipe the tab's session down to the login, ready for another company. */
export function clearSessionForCompanySwitch() {
  try {
    const kept = {};
    SWITCH_KEPT_SESSION_KEYS.forEach((key) => {
      const value = sessionStorage.getItem(key);
      if (value !== null) kept[key] = value;
    });
    sessionStorage.clear();
    Object.entries(kept).forEach(([key, value]) => sessionStorage.setItem(key, value));
  } catch (e) {
    // see setCompanyId
  }
}
